import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    console.time('[PDP] Add to cart');
    this.loading(true);
    //this.animateAddToCart();

    // if (!event.target.closest('.quick-add-modal')) this.#animateFlyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };


  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  loading(show, success){
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if(show){
      if (!addToCartButton.classList.contains('atc-loading')) {
        addToCartButton.classList.add('atc-loading');
      }
    }else{
      console.timeEnd('[PDP] Add to cart');
      addToCartButton.classList.remove('atc-loading');
      // Added:
      if(success){
        this.animateAddToCart();
      }
    }
  }
  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const _atcSource = this.closest('cart-upsell')
      ? 'Complete Your Routine'
      : this.closest('cart-drawer-component')
        ? 'minicart'
        : this.refs.addToCartButtonContainer?.dataset.isPdp === 'true'
          ? 'PDP'
          : 'minicart';
    const _atcLabel = `[ATC] to cart from ${_atcSource}`;
    console.time(_atcLabel);

    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);

    // Bundle the cart-items section IDs into the add request so Shopify
    // returns the new cart HTML in the SAME round-trip. This is faster than
    // splitting it into two sequential fetches because Shopify renders the
    // section in parallel with the cart write.
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    if (cartItemComponentsSectionIds.length) {
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    }

    // Gather product info for the optimistic skeleton row in the drawer.
    const quantity = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
    const optimisticDetail = {
      variantId: formData.get('id')?.toString(),
      productId: this.dataset.productId,
      quantity,
      ...this.#getOptimisticProductInfo(),
    };

    // Optimistic open + skeleton row: fired BEFORE the network round-trip, so
    // the drawer is on screen with the user's item inside at 0 ms perceived latency.
    document.dispatchEvent(new CustomEvent('cart:adding', {
      bubbles: true,
      detail: optimisticDetail,
    }));

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, fetchCfg)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            addToCartTextError.textContent = response.message;
          }

          if ('requestIdleCallback' in window) {
            requestIdleCallback(() => this.#setLiveRegionText(response.message));
          } else {
            setTimeout(() => this.#setLiveRegionText(response.message), 0);
          }

          setTimeout(() => {
            if (addToCartTextError) addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, 10000);

          // Tell the drawer to drop the optimistic row.
          document.dispatchEvent(new CustomEvent('cart:add-failed', { bubbles: true }));

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: quantity,
              productId: this.dataset.productId,
            })
          );
          return;
        }

        // Success: flip the button state immediately (no rAF wrap — that was adding ~32 ms for no reason).
        if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
          this.refs.addToCartButtonContainer.loading(false, true);

          const addedTextElement = this.refs.addToCartButtonContainer.refs.addToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

          if ('requestIdleCallback' in window) {
            requestIdleCallback(() => this.#setLiveRegionText(addedText));
          } else {
            setTimeout(() => this.#setLiveRegionText(addedText), 0);
          }
          setTimeout(() => this.#clearLiveRegionText(), 5000);
        }

        // sections in the event → cart-items-component morphs immediately
        // using the HTML Shopify just rendered for us in the add response.
        this.dispatchEvent(
          new CartAddEvent(response, formData.get('id').toString(), {
            source: 'product-form-component',
            itemCount: quantity,
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );
      })
      .catch((error) => {
        console.error(error);
        document.dispatchEvent(new CustomEvent('cart:add-failed', { bubbles: true }));
      })
      .finally(() => {
        console.timeEnd(_atcLabel);
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Collects product title / image / price from the current DOM so the drawer
   * can render an optimistic skeleton row before the server has responded.
   * All fields are best-effort — if something isn't found, the drawer just
   * falls back to a generic placeholder.
   *
   * @returns {{ title: string|null, image: string|null, price: string|null, url: string|null }}
   */
  #getOptimisticProductInfo() {
    // Search up the DOM so product-card (collection grids / upsells) and the
    // full product page are both covered.
    const ctx =
      this.closest('product-card, .product-details, .product-grid, .quick-add-modal, main') ||
      document;

    const titleEl =
      ctx.querySelector(
        '.view-product-title, .product__title, .product-title, h1.h1, [data-product-title], .cart-items__title'
      );

    const imgEl = /** @type {HTMLImageElement|null} */ (
      ctx.querySelector(
        'product-media img, .product-media img, .product__media img, .product-card__image img, .card__media img, img.product-card__image'
      )
    );

    const priceEl = ctx.querySelector(
      'product-price .price-item--sale, product-price .price__regular .price-item, product-price .price, .price__regular .price-item, .price-item--regular, .price-current, .price'
    );

    // If the add-to-cart button itself has a product-card ancestor, use its image
    // — it's the single element the user is looking at when they click.
    const cardImg = /** @type {HTMLImageElement|null} */ (
      this.closest('product-card, .product-card, .slp-card')?.querySelector('img')
    );

    const img = cardImg || imgEl;

    // Try to capture the product URL so the skeleton row can link to the PDP
    // exactly like the real cart-items row does — matching `<a>` structure is
    // what makes the morph visually seamless.
    const urlEl = /** @type {HTMLAnchorElement|null} */ (
      this.closest('product-card, .product-card, .slp-card')?.querySelector('a[href*="/products/"]')
      || ctx.querySelector('a.view-product-title, a.product__title, a.product-title, a[data-product-title]')
    );
    let url = urlEl?.getAttribute('href') || null;
    // If we're already on a product page, use the canonical URL.
    if (!url && /^\/products\//.test(window.location.pathname)) {
      url = window.location.pathname;
    }

    return {
      title: titleEl?.textContent?.trim() || null,
      image: img?.currentSrc || img?.src || null,
      price: priceEl?.textContent?.trim().replace(/\s+/g, ' ') || null,
      url,
    };
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    // Cache layout reads upfront to avoid thrashing later
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    const offsetX = this.offsetWidth / 2;
    const offsetY = this.offsetHeight / 2;

    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offsetX,
      y: sourceRect.top + sourceRect.height / 2 - offsetY,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offsetX,
      y: destinationRect.top + destinationRect.height / 2 - offsetY,
    };

    // Precompute control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 };
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 };

    const duration = 600;
    let startTime = null;

    this.style.opacity = '1';

    // Position at the starting point immediately
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);
    this.style.setProperty('--scale', '1');

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate position along curve
      const { x, y } = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      this.style.setProperty('--x', `${x}px`);
      this.style.setProperty('--y', `${y}px`);

      // Scale down smoothly toward the end
      this.style.setProperty('--scale', `${1 - progress * 0.5}`);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Fade out, then remove
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
